import { AppSettings } from '@/app/AppSettings';
import { makeHighlightableJson } from '@/app/log-viewer/highlighting/HighlightableJson';
import {
  HighlightableString,
  makeHighlightableString,
  makeHighlightableStringWithParts,
} from '@/app/log-viewer/highlighting/HighlightableString';
import { HighlightableStringExpandableContainerState } from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { LogEntry, LogEntryType, NetworkRequestLogEntryKeyValuePairs } from '@/parsing/LogEntry';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import {
  LogEntryCellModel,
  LogEntryCellModelData,
  LogEntryCellModelDelegate,
  LogEntryCellType,
} from '../LogEntryCellModel';
import { ExternalLinkBuilder } from './components/ExternalLinkBuilder';
import { NetworkRequestBodyModelData } from './components/NetworkRequestBody';
import { KeyValuePairsDataModel } from './components/NetworkRequestKeyValuePairDetails';

export interface LogEntryNetworkCellModelData extends LogEntryCellModelData {
  outcome: NetworkRequestOutcome;
  entryIndex: number;

  request?: {
    title: HighlightableString;

    jumpToResponseLink?: {
      responseLogEntryIndex: number;
      text: HighlightableString;
    };

    canceled?: HighlightableString;

    headers?: KeyValuePairsDataModel;
    query?: KeyValuePairsDataModel;
    body?: NetworkRequestBodyModelData;
  };

  response?: {
    title: HighlightableString;

    jumpToRequestLink?: {
      requestLogEntryIndex: number;
      text: HighlightableString;
    };

    canceled?: HighlightableString;

    httpStatusCode?: number;
    httpStatus?: HighlightableString;
    duration?: HighlightableString;

    code: HighlightableString | undefined;
    message: HighlightableString | undefined;
    additionalInfo: HighlightableString | undefined;

    headers?: KeyValuePairsDataModel;
    body?: NetworkRequestBodyModelData;
  };

  url?: HighlightableString;

  kibanaLink?: {
    url: string;
    text: HighlightableString;
  };

  googleMapsLink?: {
    url: string;
    text: HighlightableString;
  };
}

export interface LogEntryNetworkCellModel extends LogEntryCellModel {
  data: LogEntryNetworkCellModelData;

  applySettings(s: AppSettings): void;
  onRequestCreateNewColumn?: (jqQuery: string) => void;
  onRequestJumpToLogEntry?: (logEntryIndex: number) => void;
  onRequestSurroundingEntries?: (
    entryIndex: number,
    direction: 'before' | 'after',
    count: number,
  ) => LogEntry[];
  showContextualEvents: boolean;
}

export abstract class LogEntryNetworkCellModelFactory {
  static make(logEntry: LogEntry): LogEntryNetworkCellModel {
    return new LogEntryNetworkCellModelImpl(logEntry);
  }
}

class LogEntryNetworkCellModelImpl implements LogEntryNetworkCellModel {
  data: LogEntryNetworkCellModelData;
  delegate?: LogEntryCellModelDelegate;

  private logEntry: LogEntry;

  onRequestCreateNewColumn?: (jqQuery: string) => void;
  onRequestJumpToLogEntry?: (logEntryIndex: number) => void;
  onRequestSurroundingEntries?: (
    entryIndex: number,
    direction: 'before' | 'after',
    count: number,
  ) => LogEntry[];

  showContextualEvents: boolean = false;

  constructor(logEntry: LogEntry) {
    this.logEntry = logEntry;

    let isRequest: boolean;
    switch (this.logEntry.entryType) {
      case LogEntryType.NetworkRequest:
        isRequest = true;
        break;
      case LogEntryType.NetworkResponse:
        isRequest = false;
        break;
      default:
        console.error('Invalid log entry type', this.logEntry);
        throw 'Invalid log entry type';
    }

    const extra = logEntry.extra.networkRequest!;
    const googleMapsLink = ExternalLinkBuilder.buildGoogleMapsLinkFromNetworkExtra(extra);

    this.data = {
      type: isRequest ? LogEntryCellType.NetworkRequest : LogEntryCellType.NetworkResponse,
      outcome: extra.outcome,
      entryIndex: logEntry.index,

      kibanaLink: {
        url: ExternalLinkBuilder.buildKibanaLinkFromNetworkExtra(extra),
        text: makeHighlightableString('Kibana'),
      },

      googleMapsLink: googleMapsLink
        ? {
            url: googleMapsLink,
            text: makeHighlightableString('Google Maps'),
          }
        : undefined,

      url: extra.url
        ? makeHighlightableStringWithParts(
            {
              content: `${extra.url.protocol}//${extra.url.host}`,
              cssClasses: ['url', 'url-prefix'],
            },
            {
              content: '',
              cssClasses: ['url', 'url-space-before-path'],
            },
            {
              content: extra.url.pathname,
              cssClasses: ['url'],
            },
          )
        : undefined,
    };

    if (isRequest) {
      this.data.request = {
        title: makeHighlightableString(`Request #${extra.requestUniqueIndex}`),

        jumpToResponseLink: extra.response?.logEntryIndex
          ? {
              responseLogEntryIndex: extra.response.logEntryIndex,
              text: makeHighlightableString('(jump to response)'),
            }
          : undefined,

        headers: extra.request?.headers
          ? {
              title: makeHighlightableString('Request headers'),
              expandingState: HighlightableStringExpandableContainerState.Collapsed,
              shouldExpandForHighlighting: true,
              entries: this.createHighlightableStringsFromKeyValuePairs(
                extra.request.headers,
                ': ',
              ),
            }
          : undefined,

        query: extra.request?.query
          ? {
              title: makeHighlightableString('Request query'),
              expandingState: HighlightableStringExpandableContainerState.Collapsed,
              shouldExpandForHighlighting: true,
              entries: this.createHighlightableStringsFromKeyValuePairs(extra.request.query, '='),
            }
          : undefined,

        body: this.createBodyData({
          body: extra.request?.body,
          bodyJson: extra.request?.bodyJson,
          isRequest: true,
        }),
      };
    } else {
      this.data.response = {
        title: makeHighlightableString(`Response #${extra.requestUniqueIndex}`),

        jumpToRequestLink: extra.request?.logEntryIndex
          ? {
              requestLogEntryIndex: extra.request.logEntryIndex,
              text: makeHighlightableString('(jump to request)'),
            }
          : undefined,

        httpStatusCode: extra.response?.httpCode,
        httpStatus:
          typeof extra.response?.httpCode !== 'undefined'
            ? makeHighlightableStringWithParts(
                {
                  content: 'HTTP status code: ',
                  cssClasses: ['propertyKey'],
                },
                {
                  content: `${extra.response.httpCode}`,
                },
              )
            : undefined,

        duration: extra.response?.durationSec
          ? makeHighlightableStringWithParts(
              {
                content: 'Duration: ',
                cssClasses: ['propertyKey'],
              },
              {
                content: `${extra.response.durationSec.toFixed(3)} sec`,
              },
            )
          : undefined,

        code:
          extra.response?.bodyJson && typeof extra.response?.bodyJson.code !== 'undefined'
            ? makeHighlightableStringWithParts(
                {
                  content: '"code": ',
                  cssClasses: ['propertyKey'],
                },
                {
                  content: `${extra.response.bodyJson.code}`,
                  cssClasses: ['propertyValue'],
                },
              )
            : undefined,

        message:
          extra.response?.bodyJson && extra.response?.bodyJson.message
            ? makeHighlightableStringWithParts(
                {
                  content: '"message": ',
                  cssClasses: ['propertyKey'],
                },
                {
                  content: `${extra.response.bodyJson.message}`,
                  cssClasses: ['propertyValue'],
                },
              )
            : undefined,

        additionalInfo: extra.response?.additionalInfo
          ? makeHighlightableStringWithParts(
              {
                content: 'Additional info: ',
                cssClasses: ['propertyKey'],
              },
              {
                content: `${extra.response.additionalInfo}`,
                cssClasses: ['propertyValue'],
              },
            )
          : undefined,

        headers: extra.response?.headers
          ? {
              title: makeHighlightableString('Response headers'),
              expandingState: HighlightableStringExpandableContainerState.Collapsed,
              shouldExpandForHighlighting: true,
              entries: this.createHighlightableStringsFromKeyValuePairs(
                extra.response.headers,
                ': ',
              ),
            }
          : undefined,

        body: this.createBodyData({
          body: extra.response?.body,
          bodyJson: extra.response?.bodyJson,
          isRequest: false,
        }),
      };
    }
  }

  applySettings(_s: AppSettings) {}

  private createBodyData(args: {
    body: string | undefined;
    bodyJson: any;
    isRequest: boolean;
  }): NetworkRequestBodyModelData | undefined {
    if (typeof args.body !== 'undefined') {
      const title = makeHighlightableString(args.isRequest ? 'Request body' : 'Response body');

      if (typeof args.bodyJson !== 'undefined') {
        return {
          title,
          expandingState: HighlightableStringExpandableContainerState.Collapsed,
          shouldExpandForHighlighting: true,
          json: {
            jqQuery: '',
            originalJson: args.bodyJson,
            processedJqResult: {
              jqQuery: '',
              processedHighlightableJson: makeHighlightableJson(args.bodyJson),
            },
          },
        };
      }
      return {
        title,
        expandingState: HighlightableStringExpandableContainerState.Collapsed,
        shouldExpandForHighlighting: true,
        notJson: {
          string: makeHighlightableString(args.body),
        },
      };
    }

    return undefined;
  }

  private createHighlightableStringsFromKeyValuePairs(
    pairs: NetworkRequestLogEntryKeyValuePairs,
    dividerBetweenKeyAndValue: string,
  ): HighlightableString[] {
    return Array.from(pairs.entries())
      .sort((a, b) => a[1].key.localeCompare(b[1].key))
      .map((entry) =>
        makeHighlightableStringWithParts(
          {
            content: `${entry[1].key}${dividerBetweenKeyAndValue}`,
            cssClasses: ['propertyKey'],
          },
          {
            content: entry[1].value,
            cssClasses: ['propertyValue'],
          },
        ),
      );
  }
}
